import React, {FC, Fragment, ReactNode} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {InboxModel} from '../../../../../../libs/shared/core/src/models/api/inboxModel';
import {inboxHasColor} from '../../../../../../libs/shared/core/src/models/helpers/inboxHelpers';
import {ColorDot} from '../../../../../../libs/shared/ui/src/components/utility/colorDot';
import {UserTheme} from '../../../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {StyledCountSpan, StyledGridContentDiv, StyledIconDiv} from './conversationSummaryCommonStyles';
import {
  ConversationSummaryDetailsSubsection,
  StyledDetailsSubsectionListDiv,
} from './conversationSummaryDetailsSubsection';

/*
 * Props.
 */

interface ConversationSummaryInboxesProps {
  inboxes: ReadonlyArray<InboxModel>;
}

/*
 * Style.
 */

const StyledColorDotDiv = styled(StyledIconDiv)`
  padding: 4px;
`;

/*
 * Component.
 */

export const ConversationSummaryInboxes: FC<ConversationSummaryInboxesProps> = ({inboxes}) => {
  const theme = useUserTheme();
  if (inboxes.length === 0) {
    return null;
  }

  return (
    <ConversationSummaryDetailsSubsection
      title={renderInboxesTitle(inboxes)}
      content={renderInboxesContent(inboxes, theme)}
      paddingTop={26}
    />
  );
};

function renderInboxesTitle(inboxes: ReadonlyArray<InboxModel>) {
  return (
    <Plural
      id="conversation-summary-inboxes-plural"
      value={inboxes.length}
      one={<Trans id="conversation-summary-inbox">Inbox</Trans>}
      other={
        <>
          <Trans id="conversation-summary-inboxes">Inboxes</Trans>
          <StyledCountSpan>({inboxes.length})</StyledCountSpan>
        </>
      }
    />
  );
}

function renderInboxesContent(inboxes: ReadonlyArray<InboxModel>, theme: UserTheme): ReactNode {
  return (
    <StyledDetailsSubsectionListDiv>
      {inboxes.map((inbox) => (
        <Fragment key={inbox.id}>
          <StyledColorDotDiv>
            <ColorDot color={inboxHasColor(inbox) ? inbox.color : theme.greys.shade40} size={8} />
          </StyledColorDotDiv>
          <StyledGridContentDiv>{inbox.name}</StyledGridContentDiv>
        </Fragment>
      ))}
    </StyledDetailsSubsectionListDiv>
  );
}
